import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as MoreIcon } from "../../assets/icons/3Dot.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as PlusCircleIcon } from "../../assets/icons/plus-circle.svg";
import useReaderStore from "../../context/readerStore";
import { useIsMobile } from "../../hooks/useMediaQuery";
import useResizablePanelWidth from "../../hooks/useResizablePanelWidth";
import {
  clearAskAiMessages,
  getAiAvailability,
  getAiPreferences,
  peekAiAvailability,
  peekAiPreferences,
  resetAiConversation,
} from "../../services/aiService";
import AiGeneratingStatus from "./AiGeneratingStatus";
import ReadingAssistantMenu from "./ReadingAssistantMenu";
import AskAiTab from "./tabs/AskAiTab";
import QuizTab from "./tabs/QuizTab";
import SummaryTab from "./tabs/SummaryTab";
import "./ReadingAssistant.css";

const TABS = [
  { id: "summary", Component: SummaryTab },
  { id: "askAi", Component: AskAiTab },
  { id: "quiz", Component: QuizTab },
];

/** Matches prior CSS defaults across breakpoints. */
const getDefaultRaWidth = () => {
  if (typeof window === "undefined") return 480;
  const viewport = window.innerWidth;
  if (viewport >= 1440) return 520;
  if (viewport <= 1100) return 400;
  if (viewport <= 1280) return 440;
  return 480;
};

const RA_MIN_WIDTH = 320;

/** Leave room for the right toolbar + usable reader space. */
const getMaxRaWidth = () => {
  if (typeof window === "undefined") return 720;
  return Math.min(720, Math.max(RA_MIN_WIDTH, window.innerWidth - 96));
};

const RA_WIDTH_CACHE_KEY = "reading-assistant";

const availabilityStatusFromData = (data) => {
  if (data?.ai_available) return "ready";
  if (
    data?.processing_status === "processing" ||
    data?.processing_status === "queued"
  ) {
    return "processing";
  }
  if (data?.processing_status === "failed") return "failed";
  return "unavailable";
};

/**
 * Initial UI status: only "ready" when availability is ready AND prefs are
 * already cached, so SummaryTab does not fire before target_language is known.
 */
const getInitialAvailabilityState = (documentId) => {
  const cached = documentId ? peekAiAvailability(documentId) : null;
  if (!cached) return { status: "loading", message: "" };
  const status = availabilityStatusFromData(cached);
  if (status === "ready" && !peekAiPreferences()) {
    return { status: "loading", message: "" };
  }
  return { status, message: cached.message || "" };
};

const ReadingAssistant = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    isReadingAssistantSidebarOpen,
    toggleReadingAssistantSidebar,
    documentId,
    readingAssistantActiveTab,
    clearReadingAssistantActiveTab,
  } = useReaderStore();
  const [activeTab, setActiveTab] = useState("summary");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [availability, setAvailability] = useState(() =>
    getInitialAvailabilityState(documentId),
  );
  const moreButtonRef = useRef(null);
  const requestIdRef = useRef(0);

  const { width: sidebarWidth, isResizing, handleProps } = useResizablePanelWidth(
    {
      cacheKey: RA_WIDTH_CACHE_KEY,
      defaultWidth: getDefaultRaWidth,
      minWidth: RA_MIN_WIDTH,
      maxWidth: getMaxRaWidth,
      enabled: !isMobile,
      edge: "left",
    },
  );

  const isAiReady = availability.status === "ready";

  // availability → await preferences → then ready (tabs mount; no auto-summarize)
  useEffect(() => {
    let cancelled = false;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const bootstrap = async () => {
      const cachedAvail = documentId ? peekAiAvailability(documentId) : null;
      const cachedAvailStatus = cachedAvail
        ? availabilityStatusFromData(cachedAvail)
        : null;

      // Warm caches: skip loading flash on sidebar remount.
      if (
        cachedAvailStatus === "ready" &&
        peekAiPreferences()
      ) {
        setAvailability({
          status: "ready",
          message: cachedAvail.message || "",
        });
        return;
      }

      if (cachedAvail && cachedAvailStatus !== "ready") {
        setAvailability({
          status: cachedAvailStatus,
          message: cachedAvail.message || "",
        });
        return;
      }

      setAvailability({ status: "loading", message: "" });

      try {
        let data = cachedAvail;
        if (!data) {
          data = await getAiAvailability();
        }
        if (cancelled || requestIdRef.current !== requestId) return;

        const status = availabilityStatusFromData(data);
        if (status !== "ready") {
          setAvailability({
            status,
            message: data?.message || "",
          });
          return;
        }

        // Prefs must be warm before actions so target_language is correct.
        try {
          await getAiPreferences();
        } catch {
          // Non-fatal — executeAiAction falls back to DEFAULT_AI_LANGUAGE.
        }
        if (cancelled || requestIdRef.current !== requestId) return;

        setAvailability({
          status: "ready",
          message: data?.message || "",
        });
      } catch (error) {
        if (cancelled || requestIdRef.current !== requestId) return;
        setAvailability({
          status: "error",
          message: error?.message || "",
        });
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const handleCloseMenu = useCallback(() => setIsMenuOpen(false), []);

  useEffect(() => {
    if (!readingAssistantActiveTab) return;
    const isValidTab = TABS.some((tab) => tab.id === readingAssistantActiveTab);
    if (isValidTab) {
      setActiveTab(readingAssistantActiveTab);
    }
    clearReadingAssistantActiveTab();
  }, [readingAssistantActiveTab, clearReadingAssistantActiveTab]);

  const handleTabChange = (tabId) => {
    if (!isAiReady) return;
    setActiveTab(tabId);
    setIsMenuOpen(false);
  };

  // Drop local bubbles + conversation_id; next Ask AI action auto-creates a new one.
  const handleNewChat = () => {
    clearAskAiMessages(documentId);
    resetAiConversation();
    window.dispatchEvent(new Event("reader:ask-ai-new-chat"));
  };

  const statusCopy = () => {
    const { status, message } = availability;
    if (status === "loading") {
      return t("readingAssistant.status.loading");
    }
    if (status === "processing") {
      return message || t("readingAssistant.status.processing");
    }
    if (status === "failed") {
      return message || t("readingAssistant.status.failed");
    }
    if (status === "error") {
      return t("readingAssistant.status.error", {
        message: message || t("readingAssistant.status.unavailable"),
      });
    }
    return message || t("readingAssistant.status.unavailable");
  };

  return (
    <div
      className={`reading-assistant${
        isReadingAssistantSidebarOpen ? " open" : ""
      }${isResizing ? " is-resizing" : ""}`}
      role="complementary"
      aria-label={t("readingAssistant.title")}
      style={
        isMobile
          ? undefined
          : { "--ra-sidebar-width": `${sidebarWidth}px` }
      }
    >
      {!isMobile && (
        <div
          className="reading-assistant-resize-handle"
          {...handleProps}
          aria-label={t("readingAssistant.resize")}
          title={t("readingAssistant.resizeHint")}
        >
          <span className="reading-assistant-resize-grip" aria-hidden="true" />
        </div>
      )}
      <div className="reading-assistant-header">
        <div className="reading-assistant-header-left">
          <button
            type="button"
            className="reading-assistant-icon-button"
            onClick={toggleReadingAssistantSidebar}
            aria-label={t("readingAssistant.close")}
            title={t("readingAssistant.close")}
          >
            <CloseIcon width={20} height={20} aria-hidden="true" />
          </button>
          {!isMobile && (
            <h3 tabIndex="-1">{t("readingAssistant.title")}</h3>
          )}
        </div>

        {isMobile && (
          <h3 className="reading-assistant-header-title" tabIndex="-1">
            {t("readingAssistant.title")}
          </h3>
        )}

        <div className="reading-assistant-header-right">
          {activeTab === "askAi" && isAiReady && (
            <button
              type="button"
              className="reading-assistant-new-chat-button"
              onClick={handleNewChat}
              aria-label={t("readingAssistant.askAi.newChat")}
              title={t("readingAssistant.askAi.newChat")}
            >
              <PlusCircleIcon width={16} height={16} aria-hidden="true" />
              <span>{t("readingAssistant.askAi.newChat")}</span>
            </button>
          )}
          <button
            ref={moreButtonRef}
            type="button"
            className={`reading-assistant-icon-button reading-assistant-more-button${
              isMenuOpen ? " is-active" : ""
            }`}
            aria-label={t("toolbar.moreOptions")}
            title={t("toolbar.moreOptions")}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            <MoreIcon width={16} height={16} aria-hidden="true" />
          </button>

          <ReadingAssistantMenu
            isOpen={isMenuOpen}
            onClose={handleCloseMenu}
            anchorRef={moreButtonRef}
          />
        </div>
      </div>

      <div
        className="reading-assistant-tabs"
        role="tablist"
        aria-label={t("readingAssistant.tabsLabel")}
      >
        {TABS.map(({ id }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              id={`reading-assistant-tab-${id}`}
              aria-selected={isActive}
              aria-controls={`reading-assistant-panel-${id}`}
              className={`reading-assistant-tab${isActive ? " is-active" : ""}`}
              onClick={() => handleTabChange(id)}
              disabled={!isAiReady}
            >
              {t(`readingAssistant.tabs.${id}`)}
            </button>
          );
        })}
      </div>

      <div
        className="reading-assistant-content"
        aria-busy={availability.status === "loading"}
      >
        {isAiReady ? (
          TABS.map(({ id, Component }) => (
            <div
              key={id}
              role="tabpanel"
              id={`reading-assistant-panel-${id}`}
              aria-labelledby={`reading-assistant-tab-${id}`}
              hidden={activeTab !== id}
            >
              {/* Remount Ask AI per document so chat state cannot leak across docs. */}
              <Component
                key={id === "askAi" ? documentId || "askAi" : id}
              />
            </div>
          ))
        ) : (
          <div className="reading-assistant-status">
            {availability.status === "loading" ? (
              <AiGeneratingStatus message={statusCopy()} />
            ) : (
              <p role="status">{statusCopy()}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingAssistant;
